import React, { useState, useContext, useRef, useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { pdf } from '@react-pdf/renderer';
import type { PDFPageProxy, PDFDocumentProxy } from 'pdfjs-dist';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import './InvoiceGroupCoverLetterEntry.scss';
import { IInvoiceGroupCoverLetterDocument } from '../components/InvoiceGroupCoverLetterPdf';
import InvoiceGroupCoverLetterPdf from '../components/InvoiceGroupCoverLetterPdf';
import {
  PDFDocument,
  PDFForm,
  PDFField,
  PDFPage,
  StandardFonts,
  rgb
} from 'pdf-lib';

type FieldInfo = {
  name: string;
  type: string;
  pageIndex: number;
  rect: { x: number; y: number; width: number; height: number };
};

type MarkerField = {
  name: keyof IInvoiceGroupCoverLetterDocument;
  pageIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

const MARKERS: MarkerField[] = [
  {
    name: 'letterDate',
    pageIndex: 0,
    x: 435,
    y: 714,
    width: 140,
    height: 18
  },
  {
    name: 'firstParagraph',
    pageIndex: 0,
    x: 118,
    y: 375,
    width: 450,
    height: 24
  },
  {
    name: 'projectName',
    pageIndex: 0,
    x: 10,
    y: 54,
    width: 300,
    height: 18
  },
  {
    name: 'wellName',
    pageIndex: 0,
    x: 10,
    y: 484,
    width: 300,
    height: 18
  },
  {
    name: 'parcelNumbers',
    pageIndex: 0,
    x: 10,
    y: 464,
    width: 300,
    height: 18
  },
];

interface IInvoiceCoverLetterFormFillerProps extends RouteComponentProps {
  document: IInvoiceGroupCoverLetterDocument;
}

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const InvoiceGroupCoverLetterFormFiller = (props: IInvoiceCoverLetterFormFillerProps) => {
  const invoiceCoverLetterProps = props.location?.state;
  const [pdfPageSize, setPdfPageSize] = useState({ width: 0, height: 0 });
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>({});
  const [pdfForm, setPdfForm] = useState<PDFForm | null>(null);
  const [pdfDocument, setPdfDocument] = useState<PDFDocument | null>(null);
  const [htmlFields, setHtmlFields] = useState<Array<{
    name: string;
    type: string;
    pageIndex: number;
    top: number;
    left: number;
    width: number;
    height: number;
  }> | null>(null);
  const [numPdfPages, setNumPdfPages] = useState(null);
  const [renderedHeight, setRenderedHeight] = useState<number>(0);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [activeField, setActiveField] = useState<MarkerField | null>(null);
  const [draftValue, setDraftValue] = useState('');

  const renderWidth = 1000;
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const pdfPageRef = useRef<HTMLDivElement | null>(null);
  const appContext = useContext<IAppContextState>(AppContext);

  const [sampleInvoiceGroupCoverLetter, setSampleInvoiceGroupCoverLetter] =
    useState<IInvoiceGroupCoverLetterDocument>({
      letterDate: 'October 2, 2025 02:07 PM',
      groupIrNumber: '6940',
      clientName: 'Coterra Energy Inc.,',
      clientContact: 'Coterra Energy Inc.',
      clientAddress: '2000 Park Lane, Suite 300',
      clientAddress2: '2000 Park Lane, Suite 300',
      clientAddress3: '2000 Park Lane, Suite 300',
      clientCity: 'Pittsburgh',
      clientState: 'PA',
      clientZip: '15275',
      qlsNumber: '1387.2.93',
      salutation: 'Dear Valued Client',
      signatureName: 'Brittany Plunkett',
      signatureTitle: 'Billing Clerk',
      projectName: 'Susquehanna Abstracting Project',
      department: 'Auburn',
      wellName: '216.00-1,021.01,000 (Allan Garrison)',
      parcelNumbers: '1, 2, 3',
      municipalities: '',
      afeNumber: '12345',
      secondParagraph : 'This invoice group covers all costs for the above named project. Please contact me if you have any concerns with the enclosed.',
      firstParagraph: 'Please find enclosed with this letter, invoice group 5938 covering the following:'
    });

  useEffect(() => {
    if (invoiceCoverLetterProps) {
      // setSampleInvoiceGroupCoverLetter(invoiceCoverLetterProps as IInvoiceGroupCoverLetterDocument);
      console.log(invoiceCoverLetterProps);
    }
  }, [invoiceCoverLetterProps]);

  useEffect(() => {
    if (!numPdfPages) {
      setNumPdfPages(0);
    }

    renderInvoiceGroupCoverLetterPdf()
      .then((res) => res.arrayBuffer())
      .then(setPdfBytes);
  }, [sampleInvoiceGroupCoverLetter]);

  useEffect(() => {
    if (!!pdfBytes) {
      loadPdfDocument();
      console.log(appContext.currentUser.username);
    }
  }, [pdfBytes]);

  useEffect(() => {
    if (!!pdfForm) {
      setHtmlFields(getHtmlFields());
    }
  }, [pdfForm]);

  useEffect(() => {
    if (!!pdfDocument) {
      setPdfForm(pdfDocument.getForm());
    }
  }, [pdfDocument]);

  useEffect(() => {
    if (pdfPageRef.current) {
      const height = pdfPageRef.current.clientHeight;
      setRenderedHeight(height);
    }
  }, [pdfPageRef]);

  const onDocumentLoadSuccess = (pdfDoc: PDFDocumentProxy) => {
    pdfDoc.getPage(1).then((page: PDFPageProxy) => {
      const viewport = page.getViewport({ scale: 1 });
      setPdfPageSize({ width: viewport.width, height: viewport.height });
    });
  };

  const handlePageRenderSuccess = () => {
    if (viewerRef.current) {
      const canvas = viewerRef.current.querySelector('canvas');
      if (canvas) {
        setRenderedHeight(canvas.offsetHeight);
      }
    }
  };

  const loadPdfDocument = async () => {
    const pdfDoc = await PDFDocument.load(pdfBytes);
    setPdfDocument(pdfDoc);
  };

  const renderInvoiceGroupCoverLetterPdf = async () => {
    return await pdf(
      <InvoiceGroupCoverLetterPdf invoice={sampleInvoiceGroupCoverLetter} />
    ).toBlob();
  };

  const saveFilledPDF = async () => {
    if (!pdfBytes || !htmlFields) {
      return;
    }

    const pdfDoc = await PDFDocument.load(pdfBytes);
    const form = pdfDoc.getForm();
    const pages = pdfDoc.getPages();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    htmlFields.forEach((field) => {
      const value = inputValues[field.name] || '';

      try {
        const pdfField = form.getTextField(field.name);
        pdfField.setText(value);
      } catch {
        const page = pages[field.pageIndex || 0];
        page.drawText(value, {
          x: field.left,
          y: field.top,
          size: 12,
          font,
          color: rgb(0, 0, 0),
          maxWidth: field.width,
          lineHeight: field.height
        });
      }
    });

    const filledPdfBytes = await pdfDoc.save();
    const blob = new Blob([filledPdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'filled.pdf';
    link.click();
  };

  const getHtmlFields = () => {
    const pages = pdfDocument.getPages();
    const pdfFields = getFieldsInfo();

    if (!pdfFields || !pages.length) {
      return [];
    }

    const localHtmlFields = pdfFields.map((field) => {
      if (!field.rect || typeof 0 !== 'number') {
        return null;
      }

      const { x, y, width, height } = field.rect;
      const page = pages[0];
      const pageHeight = page.getSize().height;

      return {
        name: field.name,
        type: field.type,
        pageIndex: 0,
        top: pageHeight - y - height,
        left: x,
        width,
        height
      };
    });

    return localHtmlFields.filter(Boolean);
  };

  interface IPdfWidget {
    getRectangle: () => { x: number; y: number; width: number; height: number };
    P: () => unknown;
  }

  const getFieldsInfo = (): FieldInfo[] => {
    const pages = pdfDocument.getPages();
    const pdfFields = pdfForm.getFields();

    const fieldEntries = pdfFields.map((field: PDFField) => {
      const name = field.getName();
      const type = field.constructor.name;

      const acroField = (field as unknown as { acroField: { getWidgets: () => IPdfWidget[] } }).acroField;
      const widgets = acroField?.getWidgets?.() || [];

      return widgets.map((widget) => {
        const rect = widget.getRectangle();
        const pageRef = widget.P();
        const pageIndex = pages.findIndex((page: PDFPage) => page.ref === pageRef);

        if (!rect || pageIndex === -1 || pageIndex > 0) {
          return null;
        }

        const { x, y, width, height } = rect;

        return {
          name,
          type,
          pageIndex,
          rect: { x, y, width, height }
        };
      });
    });

    return ([] as FieldInfo[]).concat(...fieldEntries).filter((f): f is FieldInfo => f !== null);
  };

  return (
    <div ref={pdfPageRef}>
      <div className={'project-billing pdf-wrapper'}>
        <div style={{ position: 'relative' }} ref={viewerRef}>
          {pdfBytes && (
            <Document file={pdfBytes} onLoadSuccess={onDocumentLoadSuccess}>
              <Page
                pageNumber={1}
                width={renderWidth}
                onRenderSuccess={handlePageRenderSuccess}
              />
            </Document>
          )}

          {MARKERS.map((field) => {
            const scaleX = renderWidth / pdfPageSize.width;
            const scaleY = renderedHeight / pdfPageSize.height;

            return (
              <div
                key={field.name}
                style={{
                  position: 'absolute',
                  left: `${field.x * scaleX}px`,
                  top: `${(pdfPageSize.height - field.y) * scaleY}px`,
                  width: `${field.width * scaleX}px`,
                  height: `${field.height * scaleY}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pointerEvents: 'auto'
                }}
              >
                <button
                  title={`Edit ${field.name}`}
                  onClick={() => {
                    setActiveField(field);
                    setDraftValue(String(sampleInvoiceGroupCoverLetter[field.name] ?? ''));
                  }}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '1px solid #666',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  ?
                </button>
              </div>
            );
          })}

          {activeField && (() => {
            const scaleX = renderWidth / pdfPageSize.width;
            const scaleY = renderedHeight / pdfPageSize.height;

            const left = activeField.x * scaleX;
            const top =
              (pdfPageSize.height - activeField.y) * scaleY +
              activeField.height * scaleY +
              6;

            return (
              <input
                autoFocus={true}
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSampleInvoiceGroupCoverLetter((prev) => ({
                      ...prev,
                      [activeField.name]: draftValue
                    }));

                    setInputValues((prev) => ({
                      ...prev,
                      [activeField.name]: draftValue
                    }));

                    setActiveField(null);
                  }
                  if (e.key === 'Escape') {
                    setActiveField(null);
                  }
                }}
                style={{
                  position: 'absolute',
                  left: `${left}px`,
                  top: `${top}px`,
                  width: '240px',
                  padding: '6px 10px',
                  fontSize: '14px',
                  border: '1px solid #888',
                  borderRadius: '6px',
                  background: '#fff',
                  zIndex: 1000
                }}
                placeholder={`Enter ${activeField.name}`}
              />
            );
          })()}
        </div>
      </div>

      <button
        onClick={saveFilledPDF}
        style={{
          border: '1px solid black',
          marginTop: '20px',
          width: 'auto',
          display: 'inline-block',
          flex: '0 0 auto',
          padding: '0.5rem 1rem',
          cursor: 'pointer'
        }}
      >
        Save Filled PDF
      </button>
    </div>
  );
};

export default withRouter(InvoiceGroupCoverLetterFormFiller);


// WEBPACK FOOTER //
// ./src/components/project-billing/components/InvoiceGroupCoverLetterEntry.tsx